'use client';
import React from 'react';
import Link from 'next/link';

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  thumbnail: string;
  featured?: boolean;
}

interface BlogCardProps {
  post: BlogPost;
  index?: number;
}

export default function BlogCard({ post, index = 0 }: BlogCardProps) {
  return (
    <article
      className="blog-card group relative flex flex-col overflow-hidden rounded-[16px] transition-all duration-300"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        animationDelay: `${index * 80}ms`,
      }}
    >
      {/* Gradient border overlay on hover */}
      <div
        className="absolute inset-0 rounded-[16px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(229,134,33,0.15), rgba(224,68,43,0.1))',
          border: '1px solid transparent',
          backgroundClip: 'padding-box',
        }}
      />
      {/* Hover border gradient */}
      <div
        className="absolute inset-0 rounded-[16px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          padding: '1px',
          background: 'linear-gradient(135deg, #E58621, #E0442B)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <img
          src={post.thumbnail}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Overlay gradient */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(36,36,36,0.6) 0%, transparent 60%)' }}
        />
        {/* Category Tag */}
        <span
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white tracking-wide uppercase"
          style={{
            background: '#E0442B',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.05em',
          }}
        >
          {post.category}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 gap-3">
        {/* Meta */}
        <div className="flex items-center gap-3">
          <span
            className="text-xs font-medium"
            style={{ color: '#E58621', fontFamily: "'Inter', sans-serif" }}
          >
            {post.date}
          </span>
          <span className="w-1 h-1 rounded-full bg-[rgba(255,255,255,0.2)]" />
          <span
            className="text-xs"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}
          >
            {post.readTime}
          </span>
        </div>

        {/* Title */}
        <h3
          className="text-base font-bold leading-snug text-white group-hover:text-[#E58621] transition-colors duration-200 line-clamp-2"
          style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}
        >
          {post.title}
        </h3>

        {/* Excerpt */}
        <p
          className="text-sm leading-relaxed line-clamp-3 flex-1"
          style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}
        >
          {post.excerpt}
        </p>

        {/* Read More */}
        <Link
          href={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-sm font-semibold mt-1 transition-all duration-200 group/link"
          style={{ color: '#E58621', fontFamily: "'Inter', sans-serif" }}
        >
          Read More
          <svg
            className="w-4 h-4 transition-transform duration-200 group-hover/link:translate-x-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </article>
  );
}
